// Header behaviour: mobile burger menu, mobile sub-menu expand,
// `.header--scrolled` state, and desktop mega menu/dropdown open state.

export default function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const burger = header.querySelector('#site-header-burger');
  const panel = header.querySelector('#site-header-panel');
  const MOBILE_BP = 1100;

  // Mobile menu open / close
  const openMenu = () => {
    header.classList.add('is-menu-open');
    document.body.classList.add('menu-open');
    if (burger) burger.setAttribute('aria-expanded', 'true');
    if (panel) panel.setAttribute('aria-hidden', 'false');
  };

  const closeMenu = () => {
    header.classList.remove('is-menu-open');
    document.body.classList.remove('menu-open');
    if (burger) burger.setAttribute('aria-expanded', 'false');
    if (panel) panel.setAttribute('aria-hidden', 'true');
  };

  const toggleMenu = () => {
    if (header.classList.contains('is-menu-open')) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  if (burger) burger.addEventListener('click', toggleMenu);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  });

  // Close the panel when a real navigation link (not a parent toggle) is used.
  if (panel) {
    panel.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        if (link.getAttribute('aria-haspopup') === 'true') return;
        if (link.getAttribute('href') && link.getAttribute('href') !== '#') {
          closeMenu();
        }
      });
    });
  }

  // Mobile sub-menu expand on tap: top-level items with children are always
  // dropdown triggers here (no hover state exists on mobile to reveal them).
  const mobileParents = header.querySelectorAll(
    '.site-header__mobile-nav .menu-item-has-children'
  );

  mobileParents.forEach((item) => {
    const submenu = item.querySelector(':scope > .sub-menu');
    if (!submenu) return;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'submenu-toggle';
    toggle.setAttribute('aria-label', 'Toggle sub menu');
    toggle.setAttribute('aria-expanded', 'false');

    const parentLink = item.querySelector(':scope > a');
    if (parentLink) {
      parentLink.insertAdjacentElement('afterend', toggle);
    } else {
      item.prepend(toggle);
    }

    if (parentLink) {
      parentLink.setAttribute('aria-haspopup', 'true');
      parentLink.setAttribute('aria-expanded', 'false');
    }

    const toggleSubmenu = (e) => {
      if (e) e.preventDefault();
      const isOpen = item.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (parentLink) parentLink.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    toggle.addEventListener('click', toggleSubmenu);
    if (parentLink) parentLink.addEventListener('click', toggleSubmenu);
  });

  // Reset menu state when resizing up to desktop
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (window.innerWidth > MOBILE_BP) closeMenu();
    }, 150);
  });

  // Scrolled state: collapsing the top row shifts the nav row underneath it,
  // which could close an open mega menu mid-scroll — freeze it while hovering.
  let navHovering = false;

  const updateScrolledState = () => {
    header.classList.toggle('header--scrolled', window.scrollY > 50);
  };

  const onScroll = () => {
    if (navHovering) return;
    updateScrolledState();
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  // Desktop mega menu: its panel starts below the trigger link, so a mouse
  // moving straight down briefly crosses a gap — debounce the close instead of hiding on that mouseleave.
  const CLOSE_DELAY = 250;
  const navTriggers = header.querySelectorAll(
    '.site-header__nav > ul.site-nav > .menu-item-has-children'
  );

  navTriggers.forEach((item) => {
    let closeTimer = null;

    const open = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      item.classList.add('is-open');
      navHovering = true;
    };

    const scheduleClose = () => {
      navHovering = false;
      updateScrolledState();
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        item.classList.remove('is-open');
        closeTimer = null;
      }, CLOSE_DELAY);
    };

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', scheduleClose);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', scheduleClose);
  });
}
